import { Layers, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { formatHoursToTimeString } from '@/helpers';

import { WeekDayQuickAdd } from './week-day-quick-add';
import { WeekGridCellBg } from './week-grid-cell-bg';

interface WeekDayCellProps {
  date: Date;
  projectId: number;
  trackedHours: number;
  bookedHours: number;
  /** When true, all booked assignments for this project on this day are background work. */
  isBackgroundWork: boolean;
  isWeekend: boolean;
  /** Whether the cell allows quick-add (e.g. user has create permission). */
  canQuickAdd: boolean;
  /** Click on the cell body — typically toggles the project row's expand state. */
  onCellClick?: () => void;
}

export function WeekDayCell({
  date,
  projectId,
  trackedHours,
  bookedHours,
  isBackgroundWork,
  isWeekend,
  canQuickAdd,
  onCellClick,
}: WeekDayCellProps) {
  const { t } = useTranslation('authenticated.timeTracking');
  const isEmpty = trackedHours <= 0 && bookedHours <= 0;

  return (
    <div
      role="presentation"
      onClick={onCellClick}
      className={`group relative flex min-h-[60px] cursor-pointer flex-col items-center justify-center gap-0.5 overflow-hidden border-b border-l text-center ${
        isWeekend ? 'bg-muted/30' : ''
      }`}
    >
      <WeekGridCellBg trackedHours={trackedHours} bookedHours={bookedHours} />

      {isBackgroundWork && (
        <SimpleTooltip content={t('weekView.cell.backgroundWorkTooltip')}>
          <span
            aria-label={t('weekView.cell.backgroundWorkBadgeAriaLabel')}
            className="absolute left-1 top-1 z-[1] flex h-[18px] w-[18px] items-center justify-center rounded-sm bg-foreground/10 text-foreground/80 backdrop-blur-sm"
          >
            <Layers className="h-3 w-3" />
          </span>
        </SimpleTooltip>
      )}

      <div className="relative z-[1] flex flex-col items-center gap-0.5">
        {isEmpty ? (
          <span className="text-sm text-muted-foreground/60">{t('weekView.cell.empty')}</span>
        ) : (
          <>
            <span className="text-sm font-semibold tabular-nums text-foreground">
              {formatHoursToTimeString(trackedHours)}
            </span>
            {bookedHours > 0 && (
              <span className="text-[10.5px] tabular-nums text-muted-foreground">
                {t('weekView.cell.ofBooked', { booked: formatHoursToTimeString(bookedHours) })}
              </span>
            )}
          </>
        )}
      </div>

      {canQuickAdd && (
        <WeekDayQuickAdd
          date={date}
          projectId={projectId}
          bookedHours={bookedHours}
          trigger={
            <Button
              type="button"
              size="icon"
              variant="outline"
              aria-label={t('weekView.cell.addAriaLabel')}
              onClick={e => e.stopPropagation()}
              className="absolute right-1 top-1 z-[2] h-[22px] w-[22px] opacity-0 transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100"
            >
              <Plus className="h-3 w-3" />
            </Button>
          }
        />
      )}
    </div>
  );
}
